import { Suspense, useMemo, useState } from "react"
import { CountrySelection, Loader, MultiWidget, Stack } from "@tm/components"
import { useUser } from "@tm/context-distribution"
import { getComponentFromBundle } from "@tm/utils"
import { useLocalization } from "@tm/localization"
import { UniSearchField } from "@bundles/parts/components/_shared/UniSearchField"
import { VehicleLookupConfig, VehicleType } from "@tm/models"
import { useSelectedVehicleLookup } from "@bundles/vehicle/data/hooks"
import ExternalCatalogsWidget from "../../../external-catalogs/component"
import { startTemplateElekatChannel } from "./channel"
import DemoModuleWidgetComponent from "../../../demo-module-widget"
import {
    ColumnBox,
    LogoLandingPage,
    MoodPicture,
    RightRow,
    RowStack,
    StyledCustomerSearchWidget,
    StyledDirectSearchWidget,
    StyledStack,
    StyledVehicleWidget,
    StyledVoucherWidget,
} from "./StyledComponents"

const DemoModules = getComponentFromBundle(DemoModuleWidgetComponent, startTemplateElekatChannel)

function BikeSearch() {
    return <StyledVehicleWidget hideCover indicator={false} vehicleTypeSelectionStyle="dropdown" defaultVehicleType={8} />
}

function CustomerSearch() {
    return <StyledCustomerSearchWidget listRoute="/:workTaskId/customer/list" detailsRoute="/:workTaskId/customer/details/:customerId?/" />
}

function DirectSearch() {
    return <StyledDirectSearchWidget directSearchRoute="/:workTaskId/parts/direct/list/direct" contentOnly />
}

function CommercialVehicleSearch() {
    return <StyledVehicleWidget hideCover indicator={false} vehicleTypeSelectionStyle="dropdown" defaultVehicleType={2} />
}

function UniPartsSearch() {
    return <UniSearchField universalSearchRoute="/:workTaskId/parts/universal/list/unisearch" />
}

function VehicleSearch() {
    const [selectedSearchConfig, setSelectedSearchConfig] = useState<VehicleLookupConfig>()

    return (
        <Stack direction="row" gap={0.5}>
            <CountrySelection
                onSelectSearchOption={setSelectedSearchConfig}
                selectedSearchConfig={selectedSearchConfig}
                vehicleType={VehicleType.PassengerCar}
                useSelectedVehicleLookup={useSelectedVehicleLookup}
                flagPath="/styles/base/images/flags/"
                sx={{ maxHeight: "35px" }}
            />
            <StyledVehicleWidget hideCover indicator={false} vehicleTypeSelectionStyle="dropdown" />
        </Stack>
    )
}

export function StartTemplateElekat() {
    return (
        <Suspense fallback={<Loader />}>
            <StartTemplateElekatComponent />
        </Suspense>
    )
}

const ELEKAT_TRADER_1070 = 1070

function StartTemplateElekatComponent() {
    const { translateText } = useLocalization()
    const { userContext } = useUser() ?? {}
    const urlLogoMoodPicture = userContext?.parameter.urlLogoMoodPicture
    const urlLogoLandingPage = userContext?.parameter.urlLogoLandingPage || ""
    const traderId = userContext?.principal?.traderId

    const widgets = useMemo(() => {
        return [
            { key: "articleNumber", label: translateText(389), tabContent: DirectSearch },
            { key: "vehicle", label: translateText(1234), tabContent: VehicleSearch },
            { key: "uniPartsSearch", label: translateText(1009), tabContent: UniPartsSearch },
            { key: "bike", label: translateText(730), tabContent: BikeSearch },
            { key: "commercialVehicle", label: translateText(1915), tabContent: CommercialVehicleSearch },
            { key: "customer", label: translateText(107), tabContent: CustomerSearch },
        ] as const
    }, [translateText])

    return (
        <StyledStack direction="row" columnGap={8}>
            {/* Top Column with Logo */}
            <Stack direction="column" flex={1}>
                <LogoLandingPage src={urlLogoLandingPage} />
                <Stack direction="row">
                    {/* Left Row */}
                    <RowStack gap={2} overflow="hidden">
                        <MultiWidget widgets={widgets} activeTabKey={traderId === ELEKAT_TRADER_1070 ? "vehicle" : "articleNumber"} />
                        <StyledVoucherWidget hideCover />
                    </RowStack>

                    {/* Mid Row */}
                    <RowStack gap={2}>
                        <ColumnBox>
                            <ExternalCatalogsWidget
                                display="START"
                                itemClassName="item-horizontal"
                                listClassName="layout-horizontal cols-1"
                                size="4x1"
                                variableHeight={false}
                            />
                        </ColumnBox>
                        <DemoModules moduleRoute="/modal/^:module/compare" size="12x3" showWidget />
                    </RowStack>
                </Stack>
            </Stack>

            {/* Right Row */}
            <RightRow>
                <MoodPicture imageUrl={urlLogoMoodPicture} />
            </RightRow>
        </StyledStack>
    )
}
